// src/pages/search/GlobalSearchPage.tsx
// Global Search — the person-facing home of the search module. New route;
// the previous /search-prefixed paths (`/searchs`) belonged to the admin
// index-configuration scaffold, not to a real search experience — Search
// and Suggest had no call site anywhere in the app (see docs/ux/search-ia.md
// for the false-positive coverage note). This page is the first real one.

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '@react/hooks';
import { Badge } from '@react/components';
import { searchApi } from '@core/api';
import { suggest } from '@/features/search/logic';
import { errMessage } from '@/features/search/format';
import { urnToRoute } from '@/features/resources/logic';
import type { SearchResult, SearchFacetResult, SearchSuggestion } from '@core/types/search';
import { AnchoredMenu } from '@/components/AnchoredMenu';

export const GlobalSearchPage = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') ?? '';

  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState(initialQuery);
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [facets, setFacets] = useState<SearchFacetResult[]>([]);
  const [activeEntityTypes, setActiveEntityTypes] = useState<string[]>([]);
  const [includeFederated, setIncludeFederated] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [queryTimeMs, setQueryTimeMs] = useState(0);
  const suggestDebounce = useRef<number>();

  // Focus the search box on mount without the autoFocus prop (blocked by
  // jsx-a11y/no-autofocus) — this page's whole reason to exist is the
  // search box, so focusing it programmatically on entry is appropriate.
  useEffect(() => { inputRef.current?.focus(); }, []);

  const runSearch = (q: string, entityTypes: string[], federated: boolean) => {
    if (!q.trim()) { setResults([]); setFacets([]); return; }
    setIsSearching(true);
    setError(null);
    searchApi.Search({
      query: {
        query: q,
        filters: [],
        facet_requests: ['entity_type', 'product_slug'],
        product_slugs: [],
        entity_types: entityTypes,
        highlight: true,
        snippet_length: 200,
        sort_by: '',
        include_federated: federated,
        federated_peer_ids: [],
      } as any,
      pagination: { pagination: { cursor: '', limit: 30, direction: 'forward' }, sort: [], view: 0 } as any,
    })
      .then((res) => {
        setResults(res.results?.items ?? []);
        setFacets(res.facets?.items ?? []);
        setTotalCount(res.total_count ?? 0);
        setQueryTimeMs(res.query_time_ms ?? 0);
      })
      .catch((e) => setError(errMessage(e, t('search.global.error'))))
      .finally(() => setIsSearching(false));
  };

  // Run search on mount / when navigated to with a query param, and whenever
  // filters change while a query is active.
  useEffect(() => {
    runSearch(query, activeEntityTypes, includeFederated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeEntityTypes, includeFederated]);

  const handleQueryChange = (value: string) => {
    setQuery(value);
    setShowSuggestions(true);
    if (suggestDebounce.current) window.clearTimeout(suggestDebounce.current);
    suggestDebounce.current = window.setTimeout(() => {
      suggest(value).then((res) => setSuggestions(res.suggestions?.items ?? [])).catch(() => setSuggestions([]));
    }, 200);
  };

  const submitSearch = (q: string) => {
    setShowSuggestions(false);
    setSearchParams(q ? { q } : {});
    runSearch(q, activeEntityTypes, includeFederated);
  };

  const toggleEntityType = (value: string) => {
    setActiveEntityTypes((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  };

  const openResult = (r: SearchResult) => {
    const route = urnToRoute(r.entity_type, r.entity_urn);
    if (route) navigate(route);
  };

  const entityTypeFacet = facets.find((f) => f.field === 'entity_type');
  const productFacet = facets.find((f) => f.field === 'product_slug');

  return (
    <div className="space-y-4 p-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <h1 className="sr-only">{t('search.global.title')}</h1>

      <div className="relative max-w-xl">
        <form
          onSubmit={(e) => { e.preventDefault(); submitSearch(query); }}
        >
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => handleQueryChange(e.target.value)}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => window.setTimeout(() => setShowSuggestions(false), 150)}
            placeholder={t('search.global.placeholder')}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </form>
        <AnchoredMenu
          anchor={inputRef}
          open={showSuggestions && suggestions.length > 0}
          onClose={() => setShowSuggestions(false)}
          as="ul"
          role="none"
          matchTriggerWidth
          maxHeight={320}
        >
            {suggestions.map((s, idx) => (
              <li key={`${s.entity_urn}-${idx}`}>
                <button
                  type="button"
                  onMouseDown={() => { setQuery(s.text); submitSearch(s.text); }}
                  className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
                >
                  <span className="text-foreground">{s.text}</span>
                  {s.entity_type && <span className="text-xs text-muted-foreground">{s.entity_type}</span>}
                </button>
              </li>
            ))}
        </AnchoredMenu>
      </div>

      <label className="flex items-center gap-2 text-sm text-muted-foreground">
        <input
          type="checkbox"
          checked={includeFederated}
          onChange={(e) => setIncludeFederated(e.target.checked)}
          className="h-4 w-4 rounded border-input"
        />
        {t('search.global.includeFederated')}
      </label>

      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="flex gap-6">
        <div className="min-w-0 flex-1 space-y-3">
          {query.trim() && !isSearching && (
            <p className="text-xs text-muted-foreground">
              {t('search.global.resultCount', { count: totalCount, ms: queryTimeMs })}
            </p>
          )}
          {isSearching ? (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : !query.trim() ? (
            <p className="text-sm text-muted-foreground">{t('search.global.empty')}</p>
          ) : results.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('search.global.noResults')}</p>
          ) : (
            <ul className="space-y-2">
              {results.map((r) => (
                <li key={r.entity_urn}>
                  <button
                    onClick={() => openResult(r)}
                    className="w-full rounded-md border border-border px-3 py-2.5 text-left hover:bg-accent"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-medium text-foreground">{r.title}</span>
                      <div className="flex flex-shrink-0 items-center gap-1.5">
                        {r.source === 'SEARCH_RESULT_SOURCE_FEDERATED' && (
                          <Badge variant="secondary" size="sm">{t('search.global.federatedBadge')}</Badge>
                        )}
                        <Badge variant="secondary" size="sm">{r.entity_type}</Badge>
                      </div>
                    </div>
                    {r.snippet && <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{r.snippet}</p>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {(entityTypeFacet || productFacet) && results.length > 0 && (
          <div className="w-56 flex-shrink-0 space-y-4">
            {entityTypeFacet && (
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('search.global.facetEntityType')}</p>
                <ul className="mt-1 space-y-0.5">
                  {entityTypeFacet.buckets.map((b) => (
                    <li key={b.value}>
                      <button
                        onClick={() => toggleEntityType(b.value)}
                        className={`flex w-full items-center justify-between rounded px-2 py-1 text-left text-xs ${
                          activeEntityTypes.includes(b.value) ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-accent'
                        }`}
                      >
                        <span>{b.value}</span>
                        <span>{b.count}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GlobalSearchPage;
